import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveButtonIcon } from './button-icons';

// Variantes del botón siguiendo el design system del proyecto.
//
// Los botones llenos (default y secondary) van en `primary-solid`, como en
// Effetá Apps: en claro es el mismo navy de `primary`; en oscuro un azul hondo
// con texto blanco. Enlaces, foco y pestañas siguen en `primary`. Success y
// destructive llevan su `*-fg`: en oscuro esos rellenos son claros y el texto
// blanco no llega a AA.
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-md text-sm font-medium',
    'transition-colors',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
    'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary-solid text-primary-solid-fg hover:bg-primary-solid-hover',
        secondary: 'bg-primary-solid text-primary-solid-fg hover:bg-primary-solid-hover',
        success: 'bg-success text-success-fg hover:opacity-90',
        destructive: 'bg-danger text-danger-fg hover:opacity-90',
        outline:
          'border border-border bg-transparent text-text hover:bg-surface-2',
        ghost: 'bg-transparent text-text hover:bg-surface-2',
        link: 'bg-transparent text-secondary underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

// El icono acompaña al texto, así que escala con el `size` del botón. El
// espacio entre icono y label ya lo pone el `gap-2` de las clases base.
const ICON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  icon: 'h-4 w-4',
};

/**
 * Devuelve el texto plano de los children, o `null` si el botón renderiza algo
 * que no sea texto (su propio icono, un `<Link>`, un `<Spinner />`).
 *
 * Los nodos que React no pinta (`null`, `undefined`, booleanos) no cuentan: el
 * patrón `{isPending && <Spinner />} Guardar` es texto plano mientras la
 * mutación esté quieta, y pasa a tener elemento propio cuando arranca.
 */
function readButtonLabel(children: React.ReactNode): string | null {
  const parts: string[] = [];
  let isPlainText = true;

  React.Children.forEach(children, (child) => {
    if (child === null || child === undefined) return;
    if (typeof child === 'boolean') return;
    if (typeof child === 'string' || typeof child === 'number') {
      parts.push(String(child));
      return;
    }
    isPlainText = false;
  });

  if (!isPlainText) return null;
  const label = parts.join('');
  return label.trim() === '' ? null : label;
}

// Lo único que necesitamos saber del hijo de `asChild` para inyectarle el
// icono: qué está pintando adentro.
interface SlotChildProps {
  children?: React.ReactNode;
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Icono a la izquierda del texto. Si no se pasa, el botón lo deduce de su
   * propio label (ver `resolveButtonIcon`); `null` apaga esa deducción.
   */
  icon?: LucideIcon | null;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, icon, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Con `asChild` el contenido lo pinta el hijo (`<Link>Editar</Link>`): el
    // label a deducir es el suyo y el icono va adentro, porque Slot solo admite
    // un elemento y un hermano al lado lo rompería.
    const slotChild =
      asChild && React.isValidElement<SlotChildProps>(children) ? children : null;
    const label = readButtonLabel(
      slotChild ? slotChild.props.children : children,
    );
    // `variant="link"` no es un botón: es texto dentro de una frase. Un icono
    // ahí queda suelto en medio del párrafo, así que solo se pone si el call
    // site lo pide explícitamente.
    const Icon =
      icon !== undefined ? icon : variant === 'link' ? null : resolveButtonIcon(label);
    const iconNode = Icon ? (
      <Icon className={ICON_SIZES[size ?? 'md']} aria-hidden="true" />
    ) : null;

    // Sin icono devolvemos los children tal cual, sin fragment de por medio,
    // para no romper el `React.Children.only` de Slot.
    //
    // El hueco entre icono y texto lo sigue poniendo el `gap-2` de las clases
    // base: Slot las fusiona sobre el hijo, que es el mismo elemento que ahora
    // aloja a los dos, así que no hace falta repetirlas en el clon.
    let content: React.ReactNode = children;
    if (iconNode) {
      if (asChild) {
        // `cloneElement` sin props deja intactas las del hijo (su `to`, su
        // `className`, su ref) y solo le reemplaza los children.
        content = slotChild
          ? React.cloneElement(
              slotChild,
              undefined,
              iconNode,
              slotChild.props.children,
            )
          : children;
      } else {
        content = (
          <>
            {iconNode}
            {children}
          </>
        );
      }
    }

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
